import { Controller } from "@hotwired/stimulus"

/**
 * Domain Restrictions Controller
 * 
 * Manages domain restrictions toggle functionality.
 * Handles enabling/disabling domain restrictions and removing all domains.
 * 
 * Usage: Add data-controller="domain-restrictions" to the domain restrictions section
 *        Add data-domain-restrictions-unprotect-all-url-value with the URL
 */
export default class extends Controller {
  static values = { 
    unprotectAllUrl: String,
    projectUuid: String
  }
  static targets = ["domainsList", "enabledRadio", "disabledRadio", "form"]
  
  connect() {
    // Use event delegation - listen on the element itself for change events from radios
    this.boundToggle = this.toggle.bind(this)
    this.boundSubmit = this.handleSubmit.bind(this)
    this.element.addEventListener('change', this.boundToggle)
    
    // Set initial form visibility based on checked radio
    this.updateFormVisibility()
    // Set initial icon colors
    this.updateIconColors()
    
    // Add form submit handler
    const form = this.element.querySelector('form')
    if (form) {
      form.addEventListener('submit', this.boundSubmit)
    }
    
    // Focus the domain name input field when the page loads (only if form is visible)
    if (this.hasFormTarget && this.formTarget.style.display !== 'none') {
      const domainInput = this.formTarget.querySelector('input[type="text"][name*="[name]"]')
      if (domainInput) {
        setTimeout(() => domainInput.focus(), 0)
      }
    }
  }
  
  disconnect() {
    // Clean up event listeners
    if (this.boundToggle) {
      this.element.removeEventListener('change', this.boundToggle)
    }
    const form = this.element.querySelector('form')
    if (form && this.boundSubmit) {
      form.removeEventListener('submit', this.boundSubmit)
    }
  }
  
  toggle(event) {
    // Only handle events from privacy radios
    if (!event.target.classList.contains('privacy-radio')) {
      return
    }
    
    const radio = event.target
    const isDisabled = radio.id === 'domain_restrictions_disabled' && radio.checked
    
    // Update all icon colors in the privacy section
    this.updateIconColors()
    
    if (isDisabled) {
      const hasDomains = this.hasDomains()
      
      if (hasDomains) {
        if (confirm('Are you sure you want to disable domain restrictions? All domains will be removed from the list.')) {
          this.submitUnprotectAll()
        } else {
          // User cancelled, restore to enabled
          if (this.hasEnabledRadioTarget) {
            this.enabledRadioTarget.checked = true
            this.updateFormVisibility()
            this.updateIconColors()
          }
        }
      } else {
        // No domains, just hide the list and form
        this.updateFormVisibility()
      }
    } else if (radio.id === 'domain_restrictions_enabled' && radio.checked) {
      // Turning on domain restrictions - show the domains list and form
      this.updateFormVisibility()
    }
  }
  
  updateIconColors() {
    // Find all radios in the privacy-section and update their icon colors
    const section = this.element.querySelector('.privacy-section')
    if (section) {
      section.querySelectorAll('.privacy-radio').forEach(radio => {
        const icon = radio.nextElementSibling
        if (icon && icon.classList.contains('privacy-icon')) {
          if (radio.checked) {
            icon.style.color = '#000000' // Black when checked
          } else {
            icon.style.color = '#d1d5db' // Light gray when unchecked
          }
        }
      })
    }
  }
  
  updateFormVisibility() {
    const enabledRadio = this.element.querySelector('#domain_restrictions_enabled')
    const isEnabled = enabledRadio && enabledRadio.checked
    
    if (this.hasFormTarget) {
      if (isEnabled) {
        this.formTarget.style.display = 'block'
        // Focus the input when form becomes visible
        const domainInput = this.formTarget.querySelector('input[type="text"][name*="[name]"]')
        if (domainInput) {
          setTimeout(() => domainInput.focus(), 0)
        }
      } else {
        this.formTarget.style.display = 'none'
      }
    }
    
    if (this.hasDomainsListTarget) {
      this.domainsListTarget.style.display = isEnabled ? 'block' : 'none'
    }
  }
  
  handleSubmit(event) {
    const enabledRadio = this.element.querySelector('#domain_restrictions_enabled')
    const isEnabled = enabledRadio && enabledRadio.checked
    
    if (isEnabled) {
      const domainInput = this.formTarget.querySelector('input[type="text"][name*="[name]"]')
      if (domainInput && !domainInput.value.trim()) {
        event.preventDefault()
        domainInput.classList.add('is-invalid')
        domainInput.focus()
        return false
      } else {
        // Remove invalid class if it was there
        if (domainInput) {
          domainInput.classList.remove('is-invalid')
        }
      }
    }
  }
  
  hasDomains() {
    if (!this.hasDomainsListTarget) return false
    const existingDomains = this.domainsListTarget.querySelector('.existing-domains')
    return existingDomains && existingDomains.children.length > 0
  }
  
  submitUnprotectAll() {
    const deleteForm = document.createElement('form')
    deleteForm.method = 'POST'
    deleteForm.action = this.unprotectAllUrlValue
    deleteForm.setAttribute('data-turbo-frame', '_top')
    
    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken.getAttribute('content')
      deleteForm.appendChild(csrfInput)
    }
    
    // Add method override for DELETE
    const methodInput = document.createElement('input')
    methodInput.type = 'hidden'
    methodInput.name = '_method'
    methodInput.value = 'delete'
    deleteForm.appendChild(methodInput)
    
    document.body.appendChild(deleteForm)
    deleteForm.submit()
  }
}
